import React, { useState, useEffect, useCallback } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useUser } from "../context/UserContext";
import "../css/Admin.css";

const BASE = process.env.REACT_APP_BASE_URL;
const PERFIS_OPTIONS = ["CLIENTE", "FUNCIONARIO", "GERENTE", "ADMIN"];

function getToken() {
  const stored = sessionStorage.getItem("user");
  if (!stored) return "";
  return JSON.parse(stored).access_token || "";
}
function authHeaders() {
  return { "Content-Type": "application/json", Authentication: getToken() };
}

// ─── STEP INDICATOR ───────────────────────────────────────────────────────────

function StepIndicator({ steps, current }) {
  return (
    <div className="sf-steps">
      {steps.map((label, i) => (
        <React.Fragment key={i}>
          <div className={`sf-step ${i < current ? "done" : i === current ? "active" : ""}`}>
            <div className="sf-step-circle">
              {i < current ? "✓" : i + 1}
            </div>
            <span className="sf-step-label">{label}</span>
          </div>
          {i < steps.length - 1 && (
            <div className={`sf-step-line ${i < current ? "done" : ""}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── ABA RESTAURANTES ────────────────────────────────────────────────────────

const STEPS_REST = ["Dados", "Endereço", "Confirmar"];
const FORM_REST_EMPTY = {
  nome: "", numeroRestaurante: "",
  endereco: { rua: "", numero: "", bairro: "", cidade: "", estado: "", cep: "" },
};

function AbaRestaurantes() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(FORM_REST_EMPTY);
  const [restaurantes, setRestaurantes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroId, setFiltroId] = useState("");
  const [filtroNome, setFiltroNome] = useState("");
  const [filtroCidade, setFiltroCidade] = useState("");

  const carregarRestaurantes = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}/api/v1/public/restaurantes?pageSize=100`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setRestaurantes(data.content || []);
    } catch {
      toast.error("Erro ao carregar restaurantes.");
    }
  }, []);

  useEffect(() => { carregarRestaurantes(); }, [carregarRestaurantes]);

  const set = (name, value) => {
    if (name.startsWith("e.")) {
      const field = name.slice(2);
      setForm((f) => ({ ...f, endereco: { ...f.endereco, [field]: value } }));
    } else {
      setForm((f) => ({ ...f, [name]: value }));
    }
  };
  const ch = (e) => set(e.target.name, e.target.value);

  const canNext = () => {
    if (step === 0) {
      const digitos = form.numeroRestaurante.replace(/\D/g, "");
      return form.nome.trim() !== "" && digitos.length === 13;
    }
    if (step === 1) return form.endereco.numero && form.endereco.bairro && form.endereco.cidade && form.endereco.estado;
    return true;
  };

  const handleSubmit = async () => {
    setLoading(true);
    const toastId = toast.loading("Cadastrando restaurante...");
    try {
      const payload = { ...form, numeroRestaurante: form.numeroRestaurante.replace(/\D/g, "") };
      const res = await fetch(`${BASE}/api/v1/restaurantes`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.mensagem || err.message || "Erro ao cadastrar restaurante");
      }
      toast.update(toastId, { render: "Restaurante cadastrado!", type: "success", isLoading: false, autoClose: 2500 });
      setForm(FORM_REST_EMPTY);
      setStep(0);
      carregarRestaurantes();
    } catch (err) {
      toast.update(toastId, { render: err.message || "Erro ao cadastrar", type: "error", isLoading: false, autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const limparFiltros = () => { setFiltroId(""); setFiltroNome(""); setFiltroCidade(""); };
  const restaurantesFiltrados = restaurantes.filter((r) => {
    if (filtroId && String(r.id) !== filtroId.trim()) return false;
    if (filtroNome && !r.nome.toLowerCase().includes(filtroNome.toLowerCase())) return false;
    if (filtroCidade && !(r.endereco?.cidade || "").toLowerCase().includes(filtroCidade.toLowerCase())) return false;
    return true;
  });

  return (
    <>
      {/* ── Formulário em steps ── */}
      <div className="sf-card">
        <div className="sf-header">
          <h2>Novo Restaurante</h2>
          <StepIndicator steps={STEPS_REST} current={step} />
        </div>

        <div className="sf-body">
          {/* Step 0 — Dados básicos */}
          {step === 0 && (
            <div className="sf-fields">
              <div className="sf-field sf-field-full">
                <label>Nome do Restaurante *</label>
                <input name="nome" value={form.nome} onChange={ch} placeholder="Ex: Restaurante do João" autoFocus />
              </div>
              <div className="sf-field">
                <label>Telefone do Restaurante *</label>
                <input
                  name="numeroRestaurante"
                  value={form.numeroRestaurante}
                  onChange={(e) => setForm((f) => ({ ...f, numeroRestaurante: maskNumeroRestaurante(e.target.value) }))}
                  placeholder="55 (66) 9 8123-1294"
                  maxLength={19}
                />
                {form.numeroRestaurante && form.numeroRestaurante.replace(/\D/g, "").length < 13 && (
                  <span className="sf-field-hint sf-field-hint--error">Número incompleto — informe 13 dígitos com código do país</span>
                )}
              </div>
            </div>
          )}

          {/* Step 1 — Endereço */}
          {step === 1 && (
            <div className="sf-fields">
              <div className="sf-field">
                <label>CEP</label>
                <input name="e.cep" value={form.endereco.cep} onChange={ch} placeholder="00000-000" />
              </div>
              <div className="sf-field sf-field-full">
                <label>Rua / Avenida</label>
                <input name="e.rua" value={form.endereco.rua} onChange={ch} placeholder="Ex: Av. Paulista" autoFocus />
              </div>
              <div className="sf-field">
                <label>Número *</label>
                <input name="e.numero" value={form.endereco.numero} onChange={ch} placeholder="123" />
              </div>
              <div className="sf-field">
                <label>Bairro *</label>
                <input name="e.bairro" value={form.endereco.bairro} onChange={ch} placeholder="Centro" />
              </div>
              <div className="sf-field">
                <label>Cidade *</label>
                <input name="e.cidade" value={form.endereco.cidade} onChange={ch} placeholder="São Paulo" />
              </div>
              <div className="sf-field">
                <label>Estado *</label>
                <input name="e.estado" value={form.endereco.estado} onChange={ch} placeholder="SP" maxLength={2} />
              </div>
            </div>
          )}

          {/* Step 2 — Confirmar */}
          {step === 2 && (
            <div className="sf-confirm">
              <div className="sf-confirm-section">
                <span className="sf-confirm-section-title">Dados Básicos</span>
                <div className="sf-confirm-row"><span>Nome</span><strong>{form.nome}</strong></div>
                {form.numeroRestaurante && <div className="sf-confirm-row"><span>Telefone</span><strong>{form.numeroRestaurante}</strong></div>}
              </div>
              <div className="sf-confirm-section">
                <span className="sf-confirm-section-title">Endereço</span>
                <div className="sf-confirm-row"><span>CEP</span><strong>{form.endereco.cep || "—"}</strong></div>
                <div className="sf-confirm-row"><span>Rua</span><strong>{form.endereco.rua || "—"}</strong></div>
                <div className="sf-confirm-row"><span>Número</span><strong>{form.endereco.numero}</strong></div>
                <div className="sf-confirm-row"><span>Bairro</span><strong>{form.endereco.bairro}</strong></div>
                <div className="sf-confirm-row"><span>Cidade / Estado</span><strong>{form.endereco.cidade}, {form.endereco.estado}</strong></div>
              </div>
            </div>
          )}
        </div>

        <div className="sf-actions">
          {step > 0 && (
            <button className="sf-btn-back" onClick={() => setStep((s) => s - 1)}>
              ← Anterior
            </button>
          )}
          <div style={{ flex: 1 }} />
          {step < 2 ? (
            <button className="sf-btn-next" onClick={() => setStep((s) => s + 1)} disabled={!canNext()}>
              Próximo →
            </button>
          ) : (
            <button className="sf-btn-submit" onClick={handleSubmit} disabled={loading}>
              {loading ? "Cadastrando..." : "✓ Cadastrar Restaurante"}
            </button>
          )}
        </div>
      </div>

      {/* ── Lista ── */}
      <div className="admin-card">
        <h2>Restaurantes Cadastrados</h2>
        <div className="admin-filtros">
          <input className="filtro-input" type="number" placeholder="Filtrar por ID" value={filtroId} onChange={(e) => setFiltroId(e.target.value)} />
          <input className="filtro-input" placeholder="Filtrar por nome" value={filtroNome} onChange={(e) => setFiltroNome(e.target.value)} />
          <input className="filtro-input" placeholder="Filtrar por cidade" value={filtroCidade} onChange={(e) => setFiltroCidade(e.target.value)} />
          {(filtroId || filtroNome || filtroCidade) && <button className="filtro-limpar" onClick={limparFiltros}>Limpar</button>}
          <span className="filtro-contador">{restaurantesFiltrados.length} resultado(s)</span>
        </div>
        {restaurantesFiltrados.length === 0 ? (
          <p className="admin-empty">Nenhum restaurante encontrado.</p>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>ID</th><th>Nome</th><th>Cidade</th><th>Estado</th></tr></thead>
              <tbody>
                {restaurantesFiltrados.map((r) => (
                  <tr key={r.id}>
                    <td>{r.id}</td><td>{r.nome}</td>
                    <td>{r.endereco?.cidade || "—"}</td><td>{r.endereco?.estado || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

// ─── MASKS ────────────────────────────────────────────────────────────────────

function maskNumeroRestaurante(value) {
  const d = value.replace(/\D/g, "").slice(0, 13);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)} (${d.slice(2)}`;
  if (d.length <= 5) return `${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4)}`;
  if (d.length <= 9) return `${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 5)} ${d.slice(5)}`;
  return `${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 5)} ${d.slice(5, 9)}-${d.slice(9)}`;
}

function maskCPF(value) {
  const d = value.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

function maskPhone(value) {
  const d = value.replace(/\D/g, "").slice(0, 11);
  if (!d) return "";
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 3)} ${d.slice(3, 7)}-${d.slice(7)}`;
}

// ─── ABA USUÁRIOS ─────────────────────────────────────────────────────────────

const STEPS_USR = ["Dados Pessoais", "Acesso", "Confirmar"];
const FORM_USR_EMPTY = { nome: "", email: "", cpf: "", telefone: "", perfis: [], senha: "" };

function AbaUsuarios() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(FORM_USR_EMPTY);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroId, setFiltroId] = useState("");
  const [filtroBusca, setFiltroBusca] = useState("");
  const [filtroPerfil, setFiltroPerfil] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("");
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});

  const carregarUsuarios = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}/api/v1/users/all`, { headers: authHeaders() });
      if (!res.ok) throw new Error();
      setUsuarios(await res.json());
    } catch {
      toast.error("Erro ao carregar usuários.");
    }
  }, []);

  useEffect(() => { carregarUsuarios(); }, [carregarUsuarios]);

  const ch = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const togglePerfil = (p) => {
    setForm((f) => {
      const already = f.perfis.includes(p);
      return { ...f, perfis: already ? f.perfis.filter((x) => x !== p) : [...f.perfis, p] };
    });
  };

  const canNext = () => {
    if (step === 0) return form.email.trim() && form.cpf.trim() && form.telefone.trim();
    if (step === 1) return form.perfis.length > 0 && form.senha.trim().length >= 4;
    return true;
  };

  const handleSubmit = async () => {
    setLoading(true);
    const toastId = toast.loading("Cadastrando usuário...");
    try {
      const body = {
        nome: form.nome, email: form.email, cpf: form.cpf,
        telefone: form.telefone, senha: form.senha, confirmacaoSenha: form.senha,
        perfis: form.perfis.map(perfilCod),
      };
      const res = await fetch(`${BASE}/api/v1/users`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.mensagem || err.message || "Erro ao cadastrar usuário");
      }
      toast.update(toastId, { render: "Usuário cadastrado! Ative-o na lista abaixo.", type: "success", isLoading: false, autoClose: 3000 });
      setForm(FORM_USR_EMPTY);
      setStep(0);
      carregarUsuarios();
    } catch (err) {
      toast.update(toastId, { render: err.message, type: "error", isLoading: false, autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const abrirEdicao = (usuario) => {
    setEditUser(usuario);
    setEditForm({
      nome: usuario.nome || "",
      email: usuario.email || "",
      cpf: usuario.cpf || "",
      telefone: usuario.telefone || "",
      perfis: (usuario.perfis || []).map((p) => p.descricao || p),
      senha: "",
    });
  };

  const fecharEdicao = () => { setEditUser(null); setEditForm({}); };

  const togglePerfilEdit = (p) => {
    setEditForm((f) => {
      const already = f.perfis.includes(p);
      return { ...f, perfis: already ? f.perfis.filter((x) => x !== p) : [...f.perfis, p] };
    });
  };

  const handleEditSubmit = async () => {
    const toastId = toast.loading("Salvando alterações...");
    try {
      const body = {
        nome: editForm.nome,
        email: editForm.email,
        cpf: editForm.cpf,
        telefone: editForm.telefone,
        perfis: editForm.perfis,
        senha: editForm.senha || null,
      };
      const res = await fetch(`${BASE}/api/v1/users/${editUser.id}`, {
        method: "PUT", headers: authHeaders(), body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.mensagem || err.message || "Erro ao salvar");
      }
      toast.update(toastId, { render: "Usuário atualizado!", type: "success", isLoading: false, autoClose: 2500 });
      fecharEdicao();
      carregarUsuarios();
    } catch (err) {
      toast.update(toastId, { render: err.message, type: "error", isLoading: false, autoClose: 3000 });
    }
  };

  const toggleAtivo = async (usuario) => {
    const endpoint = usuario.ativo ? "disable" : "activate";
    const toastId = toast.loading(usuario.ativo ? "Inativando..." : "Ativando...");
    try {
      const res = await fetch(`${BASE}/api/v1/users/${usuario.id}/${endpoint}`, { method: "PUT", headers: authHeaders() });
      if (!res.ok) throw new Error();
      toast.update(toastId, { render: usuario.ativo ? "Usuário inativado!" : "Usuário ativado!", type: "success", isLoading: false, autoClose: 2000 });
      carregarUsuarios();
    } catch {
      toast.update(toastId, { render: "Erro ao alterar status", type: "error", isLoading: false, autoClose: 3000 });
    }
  };

  const limparFiltros = () => { setFiltroId(""); setFiltroBusca(""); setFiltroPerfil(""); setFiltroStatus(""); };
  const usuariosFiltrados = usuarios.filter((u) => {
    if (filtroId && String(u.id) !== filtroId.trim()) return false;
    if (filtroBusca) {
      const t = filtroBusca.toLowerCase();
      if (!(u.nome || "").toLowerCase().includes(t) && !(u.email || "").toLowerCase().includes(t)) return false;
    }
    if (filtroPerfil && !(u.perfis || []).map((p) => p.descricao || p).includes(filtroPerfil)) return false;
    if (filtroStatus === "ativo" && !u.ativo) return false;
    if (filtroStatus === "inativo" && u.ativo) return false;
    return true;
  });

  return (
    <>
      {/* ── Formulário em steps ── */}
      <div className="sf-card">
        <div className="sf-header">
          <h2>Novo Usuário</h2>
          <StepIndicator steps={STEPS_USR} current={step} />
        </div>

        <div className="sf-body">
          {/* Step 0 — Dados pessoais */}
          {step === 0 && (
            <div className="sf-fields">
              <div className="sf-field sf-field-full">
                <label>Nome Completo</label>
                <input name="nome" value={form.nome} onChange={ch} placeholder="Ex: Maria Silva" autoFocus />
              </div>
              <div className="sf-field sf-field-full">
                <label>E-mail *</label>
                <input name="email" type="email" value={form.email} onChange={ch} placeholder="usuario@email.com" />
              </div>
              <div className="sf-field">
                <label>CPF *</label>
                <input
                  name="cpf"
                  value={form.cpf}
                  onChange={(e) => setForm((f) => ({ ...f, cpf: maskCPF(e.target.value) }))}
                  placeholder="000.000.000-00"
                  maxLength={14}
                />
              </div>
              <div className="sf-field">
                <label>Telefone *</label>
                <input
                  name="telefone"
                  value={form.telefone}
                  onChange={(e) => setForm((f) => ({ ...f, telefone: maskPhone(e.target.value) }))}
                  placeholder="(11) 9 9999-9999"
                  maxLength={16}
                />
              </div>
            </div>
          )}

          {/* Step 1 — Acesso */}
          {step === 1 && (
            <div className="sf-fields">
              <div className="sf-field sf-field-full">
                <label>Perfil(s) de Acesso * <span style={{ fontWeight: 400, color: "#aaa" }}>(selecione um ou mais)</span></label>
                <div className="sf-perfil-grid">
                  {PERFIS_OPTIONS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={`sf-perfil-btn ${form.perfis.includes(p) ? "selected" : ""}`}
                      onClick={() => togglePerfil(p)}
                    >
                      <span className="sf-perfil-icon">{PERFIL_ICON[p]}</span>
                      <span>{p}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="sf-field sf-field-full">
                <label>Senha Inicial * (mín. 4 caracteres)</label>
                <input name="senha" type="password" value={form.senha} onChange={ch} placeholder="••••••••" autoFocus />
              </div>
            </div>
          )}

          {/* Step 2 — Confirmar */}
          {step === 2 && (
            <div className="sf-confirm">
              <div className="sf-confirm-section">
                <span className="sf-confirm-section-title">Dados Pessoais</span>
                <div className="sf-confirm-row"><span>Nome</span><strong>{form.nome || "—"}</strong></div>
                <div className="sf-confirm-row"><span>E-mail</span><strong>{form.email}</strong></div>
                <div className="sf-confirm-row"><span>CPF</span><strong>{form.cpf}</strong></div>
                <div className="sf-confirm-row"><span>Telefone</span><strong>{form.telefone}</strong></div>
              </div>
              <div className="sf-confirm-section">
                <span className="sf-confirm-section-title">Acesso</span>
                <div className="sf-confirm-row"><span>Perfil(s)</span><strong>{form.perfis.join(", ")}</strong></div>
                <div className="sf-confirm-row"><span>Senha</span><strong>{"•".repeat(form.senha.length)}</strong></div>
                <div className="sf-confirm-row"><span>Status inicial</span><strong className="badge badge-inativo">Inativo (ative após cadastro)</strong></div>
              </div>
            </div>
          )}
        </div>

        <div className="sf-actions">
          {step > 0 && (
            <button className="sf-btn-back" onClick={() => setStep((s) => s - 1)}>
              ← Anterior
            </button>
          )}
          <div style={{ flex: 1 }} />
          {step < 2 ? (
            <button className="sf-btn-next" onClick={() => setStep((s) => s + 1)} disabled={!canNext()}>
              Próximo →
            </button>
          ) : (
            <button className="sf-btn-submit" onClick={handleSubmit} disabled={loading}>
              {loading ? "Cadastrando..." : "✓ Cadastrar Usuário"}
            </button>
          )}
        </div>
      </div>

      {/* ── Lista ── */}
      <div className="admin-card">
        <h2>Usuários Cadastrados</h2>
        <div className="admin-filtros">
          <input className="filtro-input" type="number" placeholder="Filtrar por ID" value={filtroId} onChange={(e) => setFiltroId(e.target.value)} />
          <input className="filtro-input" placeholder="Nome ou e-mail" value={filtroBusca} onChange={(e) => setFiltroBusca(e.target.value)} />
          <select className="filtro-input" value={filtroPerfil} onChange={(e) => setFiltroPerfil(e.target.value)}>
            <option value="">Todos os perfis</option>
            {PERFIS_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select className="filtro-input" value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
            <option value="">Todos os status</option>
            <option value="ativo">Ativo</option>
            <option value="inativo">Inativo</option>
          </select>
          {(filtroId || filtroBusca || filtroPerfil || filtroStatus) && <button className="filtro-limpar" onClick={limparFiltros}>Limpar</button>}
          <span className="filtro-contador">{usuariosFiltrados.length} resultado(s)</span>
        </div>
        {usuariosFiltrados.length === 0 ? (
          <p className="admin-empty">Nenhum usuário encontrado.</p>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>ID</th><th>Nome</th><th>E-mail</th><th>Perfis</th><th>Status</th><th>Ações</th></tr></thead>
              <tbody>
                {usuariosFiltrados.map((u) => (
                  <tr key={u.id}>
                    <td>{u.id}</td>
                    <td>{u.nome}</td>
                    <td>{u.email}</td>
                    <td>{(u.perfis || []).map((p) => p.descricao || p).join(", ")}</td>
                    <td><span className={`badge ${u.ativo ? "badge-ativo" : "badge-inativo"}`}>{u.ativo ? "Ativo" : "Inativo"}</span></td>
                    <td>
                      <div className="action-btns">
                        <button className="btn-sm btn-editar" onClick={() => abrirEdicao(u)}>Editar</button>
                        {u.ativo
                          ? <button className="btn-sm btn-inativar" onClick={() => toggleAtivo(u)}>Inativar</button>
                          : <button className="btn-sm btn-ativar" onClick={() => toggleAtivo(u)}>Ativar</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Modal de Edição ── */}
      {editUser && (
        <div className="edit-modal-overlay" onClick={fecharEdicao}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="edit-modal-header">
              <h3>Editar Usuário #{editUser.id}</h3>
              <button className="edit-modal-close" onClick={fecharEdicao}>✕</button>
            </div>
            <div className="edit-modal-body">
              <div className="sf-fields">
                <div className="sf-field sf-field-full">
                  <label>Nome</label>
                  <input value={editForm.nome} onChange={(e) => setEditForm((f) => ({ ...f, nome: e.target.value }))} placeholder="Nome completo" />
                </div>
                <div className="sf-field sf-field-full">
                  <label>E-mail *</label>
                  <input type="email" value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} placeholder="usuario@email.com" />
                </div>
                <div className="sf-field">
                  <label>CPF *</label>
                  <input
                    value={editForm.cpf}
                    onChange={(e) => setEditForm((f) => ({ ...f, cpf: maskCPF(e.target.value) }))}
                    placeholder="000.000.000-00"
                    maxLength={14}
                  />
                </div>
                <div className="sf-field">
                  <label>Telefone *</label>
                  <input
                    value={editForm.telefone}
                    onChange={(e) => setEditForm((f) => ({ ...f, telefone: maskPhone(e.target.value) }))}
                    placeholder="(11) 9 9999-9999"
                    maxLength={16}
                  />
                </div>
                <div className="sf-field sf-field-full">
                  <label>Perfil(s) de Acesso *</label>
                  <div className="sf-perfil-grid">
                    {PERFIS_OPTIONS.map((p) => (
                      <button
                        key={p}
                        type="button"
                        className={`sf-perfil-btn ${editForm.perfis?.includes(p) ? "selected" : ""}`}
                        onClick={() => togglePerfilEdit(p)}
                      >
                        <span className="sf-perfil-icon">{PERFIL_ICON[p]}</span>
                        <span>{p}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="sf-field sf-field-full">
                  <label>Nova Senha <span style={{ fontWeight: 400, color: "#aaa" }}>(deixe em branco para não alterar)</span></label>
                  <input
                    type="password"
                    value={editForm.senha}
                    onChange={(e) => setEditForm((f) => ({ ...f, senha: e.target.value }))}
                    placeholder="••••••••"
                  />
                </div>
              </div>
            </div>
            <div className="edit-modal-footer">
              <button className="sf-btn-back" onClick={fecharEdicao}>Cancelar</button>
              <button
                className="sf-btn-submit"
                onClick={handleEditSubmit}
                disabled={!editForm.email?.trim() || !editForm.cpf?.trim() || !editForm.telefone?.trim() || !editForm.perfis?.length}
              >
                Salvar Alterações
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── ABA VÍNCULOS ─────────────────────────────────────────────────────────────

function AbaVinculos() {
  const [restaurantes, setRestaurantes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [restauranteSel, setRestauranteSel] = useState("");
  const [usuarioSel, setUsuarioSel] = useState("");
  const [vinculados, setVinculados] = useState([]);

  const carregarRestaurantes = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}/api/v1/public/restaurantes?pageSize=100`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setRestaurantes(data.content || []);
    } catch { toast.error("Erro ao carregar restaurantes."); }
  }, []);

  const carregarUsuarios = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}/api/v1/users/all`, { headers: authHeaders() });
      if (!res.ok) throw new Error();
      setUsuarios(await res.json());
    } catch { toast.error("Erro ao carregar usuários."); }
  }, []);

  useEffect(() => { carregarRestaurantes(); carregarUsuarios(); }, [carregarRestaurantes, carregarUsuarios]);

  const carregarVinculados = useCallback(async (id) => {
    if (!id) { setVinculados([]); return; }
    try {
      const res = await fetch(`${BASE}/api/v1/restaurantes/${id}/users`, { headers: authHeaders() });
      if (!res.ok) throw new Error();
      setVinculados(await res.json());
    } catch { toast.error("Erro ao carregar usuários vinculados."); }
  }, []);

  useEffect(() => { carregarVinculados(restauranteSel); }, [restauranteSel, carregarVinculados]);

  const vincular = async () => {
    if (!restauranteSel || !usuarioSel) { toast.warn("Selecione restaurante e usuário."); return; }
    const toastId = toast.loading("Vinculando...");
    try {
      const res = await fetch(`${BASE}/api/v1/restaurantes/${restauranteSel}/users/${usuarioSel}`, { method: "POST", headers: authHeaders() });
      if (!res.ok) throw new Error();
      toast.update(toastId, { render: "Usuário vinculado!", type: "success", isLoading: false, autoClose: 2000 });
      carregarVinculados(restauranteSel);
    } catch { toast.update(toastId, { render: "Erro ao vincular usuário", type: "error", isLoading: false, autoClose: 3000 }); }
  };

  const desvincular = async (userId) => {
    const toastId = toast.loading("Desvinculando...");
    try {
      const res = await fetch(`${BASE}/api/v1/restaurantes/${restauranteSel}/users/${userId}`, { method: "DELETE", headers: authHeaders() });
      if (!res.ok) throw new Error();
      toast.update(toastId, { render: "Usuário desvinculado!", type: "success", isLoading: false, autoClose: 2000 });
      carregarVinculados(restauranteSel);
    } catch { toast.update(toastId, { render: "Erro ao desvincular usuário", type: "error", isLoading: false, autoClose: 3000 }); }
  };

  return (
    <>
      <div className="sf-card">
        <div className="sf-header">
          <h2>Gerenciar Vínculos</h2>
          <p className="sf-subtitle">Associe usuários a restaurantes para controlar o acesso</p>
        </div>
        <div className="sf-body">
          <div className="vk-selects">
            <div className="sf-field">
              <label>Restaurante</label>
              <select value={restauranteSel} onChange={(e) => setRestauranteSel(e.target.value)}>
                <option value="">Selecione um restaurante...</option>
                {restaurantes.map((r) => <option key={r.id} value={r.id}>{r.nome}</option>)}
              </select>
            </div>
            <div className="sf-field">
              <label>Usuário</label>
              <select value={usuarioSel} onChange={(e) => setUsuarioSel(e.target.value)}>
                <option value="">Selecione um usuário...</option>
                {usuarios.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nome || u.email} — {(u.perfis || []).map((p) => p.descricao || p).join(", ")}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="sf-actions">
          <div style={{ flex: 1 }} />
          <button className="sf-btn-vincular" onClick={vincular} disabled={!restauranteSel || !usuarioSel}>
            + Vincular
          </button>
        </div>
      </div>

      {restauranteSel && (
        <div className="admin-card">
          <h2>
            Usuários vinculados
            {vinculados.length > 0 && <span className="vk-count">{vinculados.length}</span>}
          </h2>
          {vinculados.length === 0 ? (
            <p className="admin-empty">Nenhum usuário vinculado ainda.</p>
          ) : (
            <div className="vk-user-list">
              {vinculados.map((u) => (
                <div key={u.id} className="vk-user-card">
                  <div className="vk-avatar">
                    {(u.nome || u.email || "?")[0].toUpperCase()}
                  </div>
                  <div className="vk-user-info">
                    <span className="vk-user-name">{u.nome || "—"}</span>
                    <span className="vk-user-detail">
                      {u.email} · {(u.perfis || []).map((p) => p.descricao || p).join(", ")}
                    </span>
                  </div>
                  <button className="btn-sm btn-desvincular" onClick={() => desvincular(u.id)}>
                    Desvincular
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function perfilCod(perfil) {
  return { CLIENTE: 1, DESENVOLVEDOR: 2, FUNCIONARIO: 3, ADMIN: 4, GERENTE: 5 }[perfil] ?? 1;
}

const PERFIL_ICON = { CLIENTE: "👤", FUNCIONARIO: "🧑‍🍳", GERENTE: "📋", ADMIN: "⚙️" };

function DevGuard({ title, children }) {
  const { userDecoded } = useUser();
  const isDev = userDecoded()?.perfis?.includes("DESENVOLVEDOR");
  if (!isDev) return <div className="admin-page"><p style={{ color: "#c00", marginTop: 40 }}>Acesso restrito ao perfil DESENVOLVEDOR.</p></div>;
  return (
    <div className="admin-page">
      <ToastContainer position="top-right" autoClose={3000} />
      <h1>{title}</h1>
      {children}
    </div>
  );
}

// ─── PÁGINAS EXPORTADAS ───────────────────────────────────────────────────────

export function AdminRestaurantes() {
  return <DevGuard title="Restaurantes"><AbaRestaurantes /></DevGuard>;
}
export function AdminUsuarios() {
  return <DevGuard title="Usuários"><AbaUsuarios /></DevGuard>;
}
export function AdminVinculos() {
  return <DevGuard title="Vínculos"><AbaVinculos /></DevGuard>;
}
